// aiuto/Aiuto.tsx — pagina d'aiuto (/aiuto): «Come si gioca» e le domande frequenti nella lingua dell'app (testi del
// lavoro G in aiuto/testi/<lingua>.json, inglese di ripiego). I numeri dei testi vengono dal server (impostazioni del
// pannello), così l'aiuto dice sempre i valori veri. Una domanda si apre anche da un link: /aiuto#quota.
import { Palco } from "../componenti/Palco.tsx";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { richiesta } from "../servizi/api.ts";
import { eBooleano, eNumero, leggiImpostazione } from "../servizi/impostazioni.ts";
import { vai } from "../servizi/percorso.ts";
import { BASE_API, IMP_APP } from "../costanti.ts";
import { blocchi, riempi, type Pezzo } from "./testo.ts";
import { Indietro } from "../componenti/Indietro.tsx";
import { ProvaDaSolo } from "./ProvaDaSolo.tsx";
import DOMANDE from "./domande.json";

export interface TestiAiuto {
  lingua: string;
  titolo: string;
  come_si_gioca: { titolo: string; passi: Record<string, { titolo: string; testo: string }> };
  sezioni: Record<string, string>;
  faq: Record<string, { d: string; r: string }>;
  supporto: Record<string, { oggetto: string; testo: string }>;
}

const TESTI = import.meta.glob<{ default: TestiAiuto }>("./testi/*.json");

/** I testi d'aiuto della lingua (o inglesi se la lingua non c'è). */
export async function caricaTestiAiuto(lingua: string): Promise<TestiAiuto> {
  const carica = TESTI[`./testi/${lingua}.json`] ?? TESTI["./testi/en.json"]!;
  return (await carica()).default;
}

/** Valori di oggi per i segnaposto (ripiego: i valori predefiniti, se il server non risponde). */
export const VALORI_PREDEFINITI: Record<string, string | number> = {
  partite_giorno: 5,
  premi_giorno: 3,
  eta_minima: 16,
  eta_adulti: 18,
  minuti_link: 60,
  soglia_segnalazioni: 3,
  ore_finestra: 720,
  ore_sospensione: -1,
  giorni_iscrizione: 7,
  partite_minime: 3,
  giorni_nome: 30,
  email_supporto: "supporto@laughdown.net",
  email_privacy: "privacy@laughdown.net",
  sito: "laughdown.net",
};
let valoriInVolo: Promise<Record<string, string | number>> | null = null;
export function valoriAiuto(): Promise<Record<string, string | number>> {
  valoriInVolo ??= richiesta<Record<string, string | number>>("/api/v1/aiuto/valori")
    .then((v) => ({ ...VALORI_PREDEFINITI, ...v }))
    .catch(() => {
      valoriInVolo = null;
      return VALORI_PREDEFINITI;
    });
  return valoriInVolo;
}

function Pezzi({ pezzi, lingua }: { pezzi: Pezzo[]; lingua: string }): ReactNode {
  return pezzi.map((p, i) => {
    if (p.tipo === "grassetto") return <strong key={i}>{p.testo}</strong>;
    if (p.tipo === "link") {
      const legale = p.percorso.startsWith("/legal/");
      return (
        <a
          key={i}
          href={legale ? `${BASE_API}${p.percorso}?lingua=${encodeURIComponent(lingua)}` : p.percorso}
          target={legale ? "_blank" : undefined}
          rel={legale ? "noopener" : undefined}
          onClick={
            legale
              ? undefined
              : (e) => {
                  e.preventDefault();
                  vai(p.percorso);
                }
          }
        >
          {p.testo}
        </a>
      );
    }
    return <span key={i}>{p.testo}</span>;
  });
}

export function TestoAiuto({ testo, valori }: { testo: string; valori: Record<string, string | number> }) {
  const { i18n } = useTranslation();
  const b = useMemo(() => blocchi(riempi(testo, valori), DOMANDE.collegamenti), [testo, valori]);
  return (
    <>
      {b.map((x, i) =>
        x.tipo === "elenco" ? (
          <ul key={i}>
            {x.voci.map((v, j) => (
              <li key={j}>
                <Pezzi pezzi={v} lingua={i18n.language} />
              </li>
            ))}
          </ul>
        ) : (
          <p key={i}>
            {x.righe.map((r, j) => (
              <span key={j}>
                {j > 0 && <br />}
                <Pezzi pezzi={r} lingua={i18n.language} />
              </span>
            ))}
          </p>
        ),
      )}
    </>
  );
}

export function Aiuto({ onIndietro }: { onIndietro: () => void }) {
  const { t, i18n } = useTranslation();
  const [testi, setTesti] = useState<TestiAiuto | null>(null);
  const [valori, setValori] = useState<Record<string, string | number>>(VALORI_PREDEFINITI);
  const clipAttive = leggiImpostazione(IMP_APP.clipAttive, false, eBooleano);
  const videoOnline = clipAttive && leggiImpostazione(IMP_APP.videoOnline, true, eBooleano); // v037
  const pushAttive = leggiImpostazione(IMP_APP.pushAttive, true, eBooleano); // v038
  const gruppiAttivi = leggiImpostazione(IMP_APP.gruppiAttivi, true, eBooleano);
  // v026: il cambio automatico del filtro è spento di base: la domanda si vede solo se il pannello lo riaccende
  const rotazione = leggiImpostazione(IMP_APP.filtriRotazioneSecondi, 0, eNumero) > 0;
  // «Prova da solo» (v019: qui, non più nella Home).
  const provaDaSolo = leggiImpostazione(IMP_APP.provaDaSoloAttiva, true, eBooleano) && leggiImpostazione(IMP_APP.giocoAttivo, true, eBooleano);
  const aperta = (() => {
    try {
      return typeof window !== "undefined" ? decodeURIComponent(window.location.hash.slice(1)) : "";
    } catch {
      return ""; // indirizzo malformato (es. #%E0): nessuna domanda aperta
    }
  })();

  useEffect(() => {
    let vivo = true;
    void caricaTestiAiuto(i18n.language).then((x) => vivo && setTesti(x));
    void valoriAiuto().then((v) => vivo && setValori(v));
    return () => {
      vivo = false;
    };
  }, [i18n.language]);

  useEffect(() => {
    if (testi && aperta) document.getElementById(`faq-${aperta}`)?.scrollIntoView({ block: "start" });
  }, [testi, aperta]);

  const sezioni = useMemo(() => {
    const accese: Record<string, boolean> = { "app.clip_attive": clipAttive, "app.gruppi_attivi": gruppiAttivi, "app.filtri_rotazione": rotazione, "app.video_online": videoOnline, "app.push_attive": pushAttive };
    const visibili = DOMANDE.faq.filter((f) => !("condizione" in f) || accese[f.condizione as string] === true);
    return [...new Set(visibili.map((f) => f.sezione))].map((s) => ({ s, voci: visibili.filter((f) => f.sezione === s) }));
  }, [clipAttive, gruppiAttivi, rotazione, videoOnline, pushAttive]);

  return (
    <div className="pagina pagina-account ld-pagina">
      <header className="ld-barra">
        <Indietro onClick={onIndietro} />
        <span />
      </header>
      <main className="account-centro aiuto">
        {!testi ? (
          <p className="nota-centrata" role="status">
            {t("generale.caricamento")}
          </p>
        ) : (
          <>
            <Palco mascotte="bot" colore="ciano" titolo={testi.titolo} />
            <section className="scheda aiuto-come">
              <h2>{testi.come_si_gioca.titolo}</h2>
              <ol className="passi">
                {DOMANDE.come_si_gioca.map((k) => (
                  <li key={k}>
                    <strong>{testi.come_si_gioca.passi[k]?.titolo}</strong>
                    <TestoAiuto testo={testi.come_si_gioca.passi[k]?.testo ?? ""} valori={valori} />
                  </li>
                ))}
              </ol>
            </section>
            {provaDaSolo && <ProvaDaSolo />}
            {sezioni.map(({ s, voci }) => (
              <section key={s} className="aiuto-sezione">
                <h2>{testi.sezioni[s]}</h2>
                {voci.map((f) =>
                  testi.faq[f.id] ? (
                    <details key={f.id} id={`faq-${f.id}`} className="scheda aiuto-domanda" open={aperta === f.id}>
                      <summary>{riempi(testi.faq[f.id]!.d, valori)}</summary>
                      <TestoAiuto testo={testi.faq[f.id]!.r} valori={valori} />
                    </details>
                  ) : null,
                )}
              </section>
            ))}
          </>
        )}
      </main>
    </div>
  );
}
